//
// Imports
//

// Types
import type { PendingFiltersDraft } from "../../../services/api/domains/pending/pending.types";

// Composables
import type { useValidationFilters } from "../../pages/validation/useValidationFilters";

/**
 * Filter Samples Dialog Composable
 *
 * Manages the filter dialog for pending samples.
 *
 * @param key - Unique key for state isolation
 * @param filtersComposable - Composable managing validation filters
 */
export const useFilterSamplesDialog = (
  key: string = "default",
  filtersComposable: ReturnType<typeof useValidationFilters>,
) => {
  //
  // State
  //
  const isOpen = useState<boolean>(`filterSamples.${key}.isOpen`, () => false);
  const loading = useState<boolean>(`filterSamples.${key}.loading`, () => false);

  //
  // Methods (Empty - to be overridden by parent)
  //

  let applyCallback = async (): Promise<void> => {
    console.warn("[useFilterSamplesDialog] apply() not set");
  };

  let clearAllCallback = async (): Promise<void> => {
    console.warn("[useFilterSamplesDialog] clearAll() not set");
  };

  //
  // Computed Properties
  //
  const componentProps = computed(() => ({
    isOpen: isOpen.value,
    filters: filtersComposable.draftFilters.value,
    filterOptions: {
      locations: [...filtersComposable.baseFiltersComposable.filterOptions.value.locations],
      instruments: [...filtersComposable.baseFiltersComposable.filterOptions.value.instruments],
      sampleTypes: [...filtersComposable.baseFiltersComposable.filterOptions.value.sampleTypes],
      visitTypes: [...filtersComposable.baseFiltersComposable.filterOptions.value.visitTypes],
      requesters: [...filtersComposable.baseFiltersComposable.filterOptions.value.requesters],
      doctors: [...filtersComposable.baseFiltersComposable.filterOptions.value.doctors],
    },
    loading: loading.value,
    onFilterUpdate: updateFilter,
    onApply: () => applyCallback(),
    onCancel: cancel,
    onClearAll: () => clearAllCallback(),
  }));

  //
  // Methods
  //

  /**
   * Open dialog and load filter options
   */
  const open = async (): Promise<void> => {
    await filtersComposable.loadFilterOptions();
    filtersComposable.draftFilters.value = {
      ...filtersComposable.appliedFilters.value,
    };
    isOpen.value = true;
  };

  /**
   * Close dialog
   */
  const close = (): void => {
    isOpen.value = false;
  };

  /**
   * Update a filter value
   */
  const updateFilter = (key: keyof PendingFiltersDraft, value: any): void => {
    filtersComposable.draftFilters.value[key] = value;
  };

  /**
   * Cancel and revert filters
   */
  const cancel = (): void => {
    filtersComposable.cancelFilters();
    close();
  };

  // Setter methods for parent to override
  const setApply = (fn: () => Promise<void>) => {
    applyCallback = fn;
  };

  const setClearAll = (fn: () => Promise<void>) => {
    clearAllCallback = fn;
  };

  return {
    // State (readonly)
    isOpen: readonly(isOpen),
    loading,

    // Computed
    componentProps,

    // Methods
    open,
    close,
    updateFilter,
    cancel,

    // Setters for parent
    setApply,
    setClearAll,
  };
};
